import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import PageHeading from 'components/page-heading';
import apiHelper from 'js/api-helper';
import SearchSection from 'components/search-section/search-section';
import TagsFilter from 'components/tags-filter/tags-filter';
import SearchForm from 'components/search-form';
import LoadingIndicator from 'components/loading-indicator';
import ErrorMessage from 'components/error-message';
import TextSkeleton from 'components/text-skeleton';
import formHelper from 'js/form-helper';
import stringHelper from 'js/string-helper';

const SearchPage = ({
  lang = {
    autocompleteSuggestionsAriaLabel: 'Foreslåtte søketermer for nettsidesøk',
  },
  heading,
  searchEndpoint,
  loadMoreEndpoint,
  autocompleteEndpoint,
  sectionPageSize,
  pageTypeFilter,
}) => {
  const [results, setResults] = useState({
    pageTypeFilter,
  });
  const [showError, setShowError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [query, setQuery] = useState(formHelper.createQuery());
  const [initDone, setInitDone] = useState(false);

  // eslint-disable-next-line no-unused-vars
  const updateQuery = (updateFunc = q => {}) => {
    setQuery(q => {
      q = Object.assign({}, q);
      updateFunc(q);
      return q;
    });
  };

  const refreshResults = () => {
    setResults(Object.assign({}, results));
  };

  const search = () => {
    setShowError(false);
    setIsLoading(true);
    apiHelper
      .execute(searchEndpoint, query)
      .then(results => {
        formHelper.updateFilterFromQuery(
          query,
          'type',
          results.pageTypeFilter.items
        );
        setResults(results);
      })
      .catch(() => {
        setShowError(true);
      })
      .finally(() => {
        setIsLoading(false);
        setIsInitialLoading(false);
      });
  };

  const loadMore = section => {
    if (section.isLoading) {
      return;
    }
    const newPage = (section.page ?? 0) + 1;
    section.showError = false;
    section.isLoading = true;
    refreshResults();
    const sectionQuery = Object.assign({}, query);
    sectionQuery.type = section.pageType;
    sectionQuery.page = newPage;
    sectionQuery.queryTrackId = results.queryTrackId;
    apiHelper
      .execute(loadMoreEndpoint, sectionQuery)
      .then(loadMoreResults => {
        section.hits = [...section.hits, ...loadMoreResults.hits];
        section.hasMore = loadMoreResults.hasMore;
        section.page = newPage;
      })
      .catch(() => {
        section.showError = true;
      })
      .finally(() => {
        section.isLoading = false;
        refreshResults();
      });
  };

  const init = () => {
    updateQuery(q => formHelper.fillQueryFromPageQueryString(q));
  };

  useEffect(() => {
    if (!initDone) {
      init();
      setInitDone(true);
    }
  }, []);

  useEffect(() => {
    if (initDone) {
      formHelper.updatePageQueryString(query);
      search();
    }
  }, [query, initDone]);

  const onPageTypeFilterChange = e => {
    updateQuery(q => formHelper.updateQueryFromFilter(q, 'type', e.items));
  };

  const sectionSkeletonItems = useMemo(() => {
    return Array.from({ length: sectionPageSize }, () => {});
  }, [sectionPageSize]);

  return (
    <div className="search-page">
      <PageHeading text={heading} className="page-heading_small" />
      <div className="search-page__container">
        <div className="search-page__top-panel">
          <div className="search-page__top-panel-search-col">
            <div className="search-page__hits-text">
              {isInitialLoading ? (
                <TextSkeleton
                  className="search-page__hits-text-skeleton"
                  lineClassName="search-page__hits-text-skeleton-line"
                  maxLines={1}
                />
              ) : results ? (
                stringHelper.format(
                  lang.searchHitsText,
                  query.q,
                  results.totalCount
                )
              ) : (
                ''
              )}
            </div>
            <SearchForm
              className="search-page__search-form"
              searchButtonText={lang.searchButtonText}
              searchInputAriaLabel={lang.searchInputAriaLabel}
              searchHint={lang.searchHint}
              searchFormAriaLabel={lang.searchFormAriaLabel}
              autocompleteSuggestionsAriaLabel={
                lang.autocompleteSuggestionsAriaLabel
              }
              query={query.q}
              onSearch={e =>
                updateQuery(q => formHelper.setQueryValue(q, 'q', e.query))
              }
              isLoading={isInitialLoading}
              autocompleteEndpoint={autocompleteEndpoint}
            />
          </div>
          <div className="search-page__top-panel-filters-col">
            {results && results.pageTypeFilter && results.totalCount !== 0 && (
              <TagsFilter
                {...results.pageTypeFilter}
                asSkeleton={isInitialLoading}
                onChange={onPageTypeFilterChange}
                itemCountPlaceholder="(111)"
                className="search-page__tags-filter"
              />
            )}
          </div>
        </div>
      </div>
      <div className="search-page__content">
        {isInitialLoading ? (
          <SearchSection
            asSkeleton={true}
            hits={sectionSkeletonItems}
            loadMoreButtonText={lang.loadMoreButtonText}
          />
        ) : (
          results && (
            <React.Fragment>
              {results.totalCount === 0 ? (
                <React.Fragment>
                  <h3 className="search-page__no-results-heading">
                    {lang.noResultsHeading}
                  </h3>
                  <div className="search-page__no-results-text">
                    {lang.noResultsText}
                  </div>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  {results.sections &&
                    results.sections.map((section, index) => (
                      <SearchSection
                        key={section.pageType}
                        {...section}
                        isFirstSection={index === 0}
                        loadMoreButtonText={lang.loadMoreButtonText}
                        errorMessage={lang.errorMessage}
                        onLoadMoreClick={() => loadMore(section)}
                      />
                    ))}
                </React.Fragment>
              )}
            </React.Fragment>
          )
        )}
        {isLoading && !isInitialLoading && <LoadingIndicator />}
        {showError && <ErrorMessage text={lang.errorMessage} />}
      </div>
    </div>
  );
};

SearchPage.propTypes = {
  lang: PropTypes.shape({
    searchHitsText: PropTypes.string.isRequired,
    noResultsHeading: PropTypes.string.isRequired,
    noResultsText: PropTypes.string.isRequired,
    searchHint: PropTypes.string.isRequired,
    searchButtonText: PropTypes.string.isRequired,
    searchInputAriaLabel: PropTypes.string.isRequired,
    loadMoreButtonText: PropTypes.string.isRequired,
    errorMessage: PropTypes.string.isRequired,
    searchFormAriaLabel: PropTypes.string.isRequired,
    autocompleteSuggestionsAriaLabel: PropTypes.string.isRequired,
  }).isRequired,
  heading: PropTypes.string,
  searchEndpoint: PropTypes.string.isRequired,
  loadMoreEndpoint: PropTypes.string.isRequired,
  autocompleteEndpoint: PropTypes.string,
  sectionPageSize: PropTypes.number,
  pageTypeFilter: PropTypes.exact(TagsFilter.propTypes),
};

export default SearchPage;
